import { Weight } from 'careit';
import { getFoodTypeLabel } from 'src/utils/foodTypes';

const DAYS_OF_THE_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];
const ALL_DAYS = DAYS_OF_THE_WEEK.map((_, index) => index);
const SHORT_DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export const RECIPIENT_MATCH_LIMIT = 10;

const finiteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizeFoodTypeName = (value) =>
  String(value || '')
    .trim()
    .replace(/[_\s]+/g, '-')
    .toUpperCase();

const normalizeWeightUnit = (unit) => {
  const normalized = String(unit || 'lbs').toLowerCase();
  if (['kg', 'kgs', 'kilogram', 'kilograms'].includes(normalized)) return 'kgs';
  if (['lb', 'lbs', 'pound', 'pounds'].includes(normalized)) return 'lbs';
  return normalized;
};

const normalizeFoodTypes = (foodTypes) => {
  if (Array.isArray(foodTypes)) {
    return foodTypes
      .map((foodType) => ({
        name: normalizeFoodTypeName(
          foodType?.name || foodType?.food_type || foodType?.title
        ),
        weightLbs: finiteNumber(
          foodType?.weightLbs ??
            foodType?.weight_lbs ??
            foodType?.estimated_weight
        ),
      }))
      .filter(({ name }) => name);
  }

  return Object.entries(foodTypes || {}).flatMap(([name, weight]) => {
    const value = finiteNumber(weight?.value);
    if (value === null) return [];

    let weightLbs = value;
    try {
      weightLbs = new Weight(value, normalizeWeightUnit(weight?.unit)).as(
        'lbs'
      );
    } catch (_error) {
      return [];
    }

    return [{ name: normalizeFoodTypeName(name), weightLbs }];
  });
};

const dayNumber = (value) => {
  const numericDay = finiteNumber(value);
  if (numericDay !== null && numericDay >= 0 && numericDay <= 6) {
    return numericDay;
  }

  const normalized = String(value || '').toLowerCase();
  const index = DAYS_OF_THE_WEEK.indexOf(normalized);
  return index >= 0 ? index : null;
};

const dateDay = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getDay();
};

const timeMinutes = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.getHours() * 60 + date.getMinutes();
};

const scheduleDays = ({
  occurrenceType,
  interval,
  daysOfTheWeek,
  dayOfTheWeek,
  dayOfTheMonth,
  startDate,
}) => {
  if (occurrenceType === 'onetime') {
    const day = dateDay(startDate);
    return day === null ? [] : [day];
  }

  const normalizedInterval = String(interval || '').toLowerCase();
  if (normalizedInterval === 'daily') return ALL_DAYS;

  if (normalizedInterval === 'weekly' || normalizedInterval === 'bi-weekly') {
    const days = (daysOfTheWeek || dayOfTheWeek || [])
      .map(dayNumber)
      .filter((day) => day !== null);
    return [...new Set(days)];
  }

  if (normalizedInterval === 'monthly') {
    const weekDay = dayNumber(dayOfTheMonth?.week?.dayOfTheWeek);
    return weekDay === null ? ALL_DAYS : [weekDay];
  }

  const fallbackDay = dateDay(startDate);
  return fallbackDay === null ? [] : [fallbackDay];
};

export const buildFoodRunRecipientMatchRequest = ({
  donorTeamId,
  pickupLocation,
  foodTypes,
  occurrenceType = 'onetime',
  interval,
  daysOfTheWeek,
  dayOfTheWeek,
  dayOfTheMonth,
  startDate,
  startTime,
  endTime,
}) => ({
  lat: finiteNumber(pickupLocation?.lat),
  lng: finiteNumber(pickupLocation?.lng),
  donorTeamId,
  foodTypes: normalizeFoodTypes(foodTypes),
  schedule: {
    days: scheduleDays({
      occurrenceType,
      interval,
      daysOfTheWeek,
      dayOfTheWeek,
      dayOfTheMonth,
      startDate,
    }),
    startMinutes: timeMinutes(startTime),
    endMinutes: timeMinutes(endTime),
    // A numeric day-of-month lands on a different weekday each month, so
    // any open weekday fits rather than every weekday being required.
    anyDay:
      occurrenceType !== 'onetime' &&
      String(interval || '').toLowerCase() === 'monthly' &&
      dayNumber(dayOfTheMonth?.week?.dayOfTheWeek) === null,
  },
});

const defaultOnboarding = (teamId) => ({
  teamId,
  isAssignable: true,
  isPending: false,
  requiresOnboarding: false,
});

export const buildRecipientOptions = ({
  myRecipients = [],
  matchedRecipients = [],
  existingRecipient,
  selfOrg,
  matchLimit = RECIPIENT_MATCH_LIMIT,
  matchedRecipientsAreRanked = true,
}) => {
  const recipientsById = new Map();
  [...myRecipients, ...matchedRecipients, existingRecipient, selfOrg]
    .filter((recipient) => recipient?.team_id)
    .forEach((recipient) => {
      const current = recipientsById.get(recipient.team_id) || {};
      recipientsById.set(recipient.team_id, { ...current, ...recipient });
    });

  const currentRecipientTeamId = existingRecipient?.team_id;
  const selfTeamId = selfOrg?.team_id;
  const myRecipientIds = new Set(myRecipients.map(({ team_id }) => team_id));
  const rankedIds = matchedRecipientsAreRanked
    ? matchedRecipients.map(({ team_id }) => team_id)
    : [];
  const bestMatchIds = new Set(
    rankedIds
      .filter(
        (teamId) =>
          teamId !== currentRecipientTeamId &&
          teamId !== selfTeamId &&
          !recipientsById.get(teamId)?.onboarding?.isPending
      )
      .slice(0, matchLimit)
  );
  const rankByTeamId = new Map(
    rankedIds.map((teamId, index) => [teamId, index])
  );

  return [...recipientsById.values()]
    .map((recipient) => {
      // Food movers can't be attached to compliance profiles, so food run
      // recipients default to assignable.
      const onboarding =
        recipient.onboarding || defaultOnboarding(recipient.team_id);
      const recipient_match_group =
        recipient.team_id === currentRecipientTeamId
          ? 'current'
          : bestMatchIds.has(recipient.team_id)
            ? 'best'
            : recipient.team_id === selfTeamId
              ? 'self'
              : myRecipientIds.has(recipient.team_id)
                ? 'my-orgs'
                : 'other';

      return { ...recipient, onboarding, recipient_match_group };
    })
    .sort((a, b) => {
      const groupOrder = {
        current: 0,
        best: 1,
        self: 2,
        'my-orgs': 3,
        other: 4,
      };
      const groupDifference =
        groupOrder[a.recipient_match_group] -
        groupOrder[b.recipient_match_group];
      if (groupDifference !== 0) return groupDifference;

      if (a.recipient_match_group === 'best') {
        return rankByTeamId.get(a.team_id) - rankByTeamId.get(b.team_id);
      }

      return String(a.name || '').localeCompare(
        String(b.name || ''),
        undefined,
        { numeric: true, sensitivity: 'base' }
      );
    });
};

export const getRecipientMatchGroupLabel = (recipient, t) => {
  if (recipient?.recipient_match_group === 'current') {
    return t('donations.current-recipient', {
      defaultValue: 'Current recipient',
    });
  }
  if (recipient?.recipient_match_group === 'best') {
    return t('donations.best-matches', { defaultValue: 'Best matches' });
  }
  if (recipient?.recipient_match_group === 'self') {
    return t('donations.your-organization-group', {
      defaultValue: 'Your organization',
    });
  }
  if (recipient?.recipient_match_group === 'my-orgs') {
    return t('donations.my-recipients-group', {
      defaultValue: 'My Recipients',
    });
  }
  return t('donations.other-recipients', { defaultValue: 'Other recipients' });
};

const formatFoodTypes = (foodTypes, t) =>
  (foodTypes || [])
    .map((foodType) => {
      const name = String(foodType?.name ?? foodType).toLowerCase();
      try {
        return getFoodTypeLabel(t, name);
      } catch (_error) {
        return name.replace(/-/g, ' ');
      }
    })
    .join(', ');

const formatMinutes = (minutes) =>
  new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60)
    .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    .replace(':00', '');

export const formatRecipientMatchReason = (reason, t) => {
  switch (reason?.code) {
    case 'food-types':
      return t('donations.match-reason-food-types', {
        defaultValue: 'Accepts {{foodTypes}}',
        foodTypes: formatFoodTypes(reason.foodTypes, t),
      });
    case 'quantity-fit':
      return t('donations.match-reason-quantity', {
        defaultValue: 'Weight fits limits for {{foodTypes}}',
        foodTypes: formatFoodTypes(reason.foodTypes, t),
      });
    case 'hours-fit': {
      const hours = {
        day: t(`common.${SHORT_DAY_KEYS[reason.day]}`),
        open: formatMinutes(reason.openMinutes),
        close: formatMinutes(reason.closeMinutes),
      };
      return reason.partial
        ? t('donations.match-reason-hours-partial', {
            defaultValue: 'Partly open: {{day}} {{open}}–{{close}}',
            ...hours,
          })
        : t('donations.match-reason-hours', {
            defaultValue: 'Open {{day}} {{open}}–{{close}}',
            ...hours,
          });
    }
    case 'hours-days':
      return t('donations.match-reason-hours-days', {
        defaultValue: 'Open all {{totalDays}} run days',
        totalDays: reason.totalDays,
      });
    case 'prior-work':
      return t('donations.match-reason-prior-work', {
        defaultValue: 'Worked with your team before',
      });
    case 'my-org':
      return t('donations.match-reason-my-org', {
        defaultValue: 'Saved in My Recipients',
      });
    case 'recent-activity':
      return t('donations.match-reason-recent', {
        defaultValue: 'Recently active',
      });
    default:
      return '';
  }
};

export const formatRecipientMatchWarning = (warning, t) => {
  switch (warning?.code) {
    case 'food-types-rejected':
      return t('donations.match-warning-food-types', {
        defaultValue: "Doesn't accept {{foodTypes}}",
        foodTypes: formatFoodTypes(warning.foodTypes, t),
      });
    case 'quantity-outside':
      return (warning.foodTypes || [])
        .map(({ name, weightLbs, minimum, maximum }) => {
          const values = {
            foodType: formatFoodTypes([name], t),
            minimum,
            maximum,
          };
          return maximum !== null && weightLbs > maximum
            ? t('donations.match-warning-over-maximum', {
                defaultValue: 'Takes up to {{maximum}} lbs {{foodType}}',
                ...values,
              })
            : t('donations.match-warning-under-minimum', {
                defaultValue: 'Needs at least {{minimum}} lbs {{foodType}}',
                ...values,
              });
        })
        .join(' · ');
    case 'closed':
      return t('donations.match-warning-closed', {
        defaultValue: 'Closed during this run',
      });
    case 'closed-days':
      return t('donations.match-warning-closed-days', {
        defaultValue: 'Closed {{closedDays}} of {{totalDays}} run days',
        closedDays: warning.closedDays,
        totalDays: warning.totalDays,
      });
    default:
      return '';
  }
};

// Distance shows on every option; reasons only on best matches; mismatch
// warnings on any option the matcher scored.
export const getRecipientMatchLabels = (recipient, t) => {
  const miles = finiteNumber(recipient?.distance);
  const reasons =
    recipient?.recipient_match_group === 'best'
      ? (recipient.match_reasons || [])
          .map((reason) => formatRecipientMatchReason(reason, t))
          .filter(Boolean)
      : [];
  const warnings = (recipient?.match_warnings || [])
    .map((warning) => formatRecipientMatchWarning(warning, t))
    .filter(Boolean);

  return {
    distance:
      miles === null
        ? ''
        : t('donations.match-distance', {
            defaultValue: '{{distance}} mi',
            distance: Math.round(miles * 10) / 10,
          }),
    reasons: reasons.join(' · '),
    warnings: warnings.join(' · '),
  };
};

const PROFILE_REASON_CODES = new Set([
  'food-types',
  'quantity-fit',
  'hours-fit',
  'hours-days',
]);

// Most recipients haven't filled in food types or hours, so say so when the
// best matches are mostly ranked on distance and history.
export const getRecipientMatchNote = (options, t) => {
  const best = options.filter(
    ({ recipient_match_group }) => recipient_match_group === 'best'
  );
  if (best.length === 0) return '';

  const withProfileReasons = best.filter(({ match_reasons }) =>
    (match_reasons || []).some(({ code }) => PROFILE_REASON_CODES.has(code))
  ).length;
  return withProfileReasons * 2 < best.length
    ? t('donations.match-note-sparse-profiles', {
        defaultValue:
          "Most nearby recipients haven't set food types or hours, so these are ranked mostly by distance and history.",
      })
    : '';
};

export const RECIPIENT_OTHER_RENDER_LIMIT = 50;

// Dense metros return over a thousand recipients. Filtering still runs over
// all of them, but only the first few "other" rows render, followed by a hint
// row telling the user to type to narrow the list.
export const capRecipientOptions = (
  options,
  { getGroup, makeHint, limit = RECIPIENT_OTHER_RENDER_LIMIT }
) => {
  let shownOther = 0;
  let hiddenOther = 0;
  const visible = options.filter((option) => {
    if (getGroup(option) !== 'other') return true;
    if (shownOther < limit) {
      shownOther += 1;
      return true;
    }
    hiddenOther += 1;
    return false;
  });
  return hiddenOther > 0 ? [...visible, makeHint(hiddenOther)] : visible;
};

export const getRecipientMoreHint = (count, t) =>
  t('donations.recipients-type-to-search', {
    defaultValue: '{{count}} more recipients — type a name to search',
    count,
  });
